// ErrorBoundary.tsx — globaali virheraja joka estää koko UI:n kaatumisen
// jos jokin komponentti heittää (esim. lazy-import-virhe, network-error,
// uncaught render-error). Näyttää siistin sivun käyttäjälle ja antaa
// "Lataa sivu uudelleen" -napin.

import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
     
    console.error('[ErrorBoundary]', error, errorInfo);
    // Sentry-lähetys jos käytössä
    try {
      const Sentry = (window as any).Sentry;
      if (Sentry?.captureException) Sentry.captureException(error);
    } catch { /* ignore */ }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    const isNetworkError = this.state.error?.message?.includes('Failed to fetch')
      || this.state.error?.message?.includes('NetworkError');

    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'inherit',
        background: '#fafaf8',
      }}>
        <div style={{
          maxWidth: 440,
          width: '100%',
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 16,
          padding: 28,
          textAlign: 'center',
          boxShadow: '0 8px 24px rgba(15, 25, 35, 0.06)',
        }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>
            {isOffline || isNetworkError ? '📡' : '⚠️'}
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f1923', margin: '0 0 8px' }}>
            {isOffline || isNetworkError ? 'Yhteysongelma' : 'Jokin meni pieleen'}
          </h2>
          <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 20px', lineHeight: 1.5 }}>
            {isOffline
              ? 'Ei verkkoyhteyttä. Tarkista nettiyhteys ja yritä uudelleen — kesken jääneet tallennukset säilyvät.'
              : isNetworkError
                ? 'Tietoja ei voitu ladata. Yhteys saattaa olla heikko.'
                : 'Sovelluksessa tapahtui odottamaton virhe. Yritä ladata sivu uudelleen.'}
          </p>
          {this.state.error?.message && !isOffline && !isNetworkError && (
            <div style={{
              fontSize: 11,
              color: '#94a3b8',
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              background: '#f8fafc',
              padding: '8px 10px',
              borderRadius: 6,
              marginBottom: 16,
              wordBreak: 'break-word',
              textAlign: 'left',
            }}>
              {this.state.error.message}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={this.handleHome} style={{
              padding: '10px 16px',
              background: 'transparent',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              color: '#64748b',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
              Etusivu
            </button>
            <button onClick={this.handleReload} style={{
              padding: '10px 18px',
              background: '#3c7c9c',
              border: 'none',
              borderRadius: 8,
              color: '#ffffff',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
              Lataa uudelleen
            </button>
          </div>
        </div>
      </div>
    );
  }
}
